import React from 'react';
import styled, { keyframes } from 'styled-components';

const LoadingSpinner = ({ 
  size = 40, 
  color = '#ff6900', 
  text = 'Loading...', 
  fullScreen = false 
}) => {
  const SpinnerComponent = fullScreen ? FullScreenContainer : InlineContainer;
  
  return (
    <SpinnerComponent>
      <SpinnerWrapper>
        <Spinner size={size} color={color} />
        {text && <LoadingText>{text}</LoadingText>}
      </SpinnerWrapper>
    </SpinnerComponent>
  );
};

// Keyframes for animations
const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

// Styled Components
const FullScreenContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const InlineContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const Spinner = styled.div`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-left: 3px solid ${props => props.color};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

// Page Loading Spinner (for route transitions)
export const PageLoadingSpinner = () => (
  <PageLoadingContainer>
    <LogoSpinner>
      <LogoImage src="/logo.png" alt="Govind Industries" />
      <SpinnerRing />
    </LogoSpinner>
    <LoadingText>Loading...</LoadingText>
  </PageLoadingContainer>
);

const PageLoadingContainer = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
`;

const LogoSpinner = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoImage = styled.img`
  height: 50px;
  width: auto;
  z-index: 2;
  position: relative;
`;

const SpinnerRing = styled.div`
  position: absolute;
  width: 80px;
  height: 80px;
  border: 3px solid rgba(255, 105, 0, 0.1);
  border-left: 3px solid #ff6900;
  border-radius: 50%;
  animation: ${spin} 2s linear infinite;
  z-index: 1;
`;

// Button Loading State
export const ButtonSpinner = ({ size = 16, color = 'currentColor' }) => (
  <ButtonSpinnerWrapper>
    <ButtonSpinnerElement size={size} color={color} />
  </ButtonSpinnerWrapper>
);

const ButtonSpinnerWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 0.5rem;
`;

const ButtonSpinnerElement = styled.div`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border: 2px solid transparent;
  border-left: 2px solid ${props => props.color};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export default LoadingSpinner;